import React, { useState, useEffect } from 'react';
import { BedZone, PressureReading } from './interfaces/types';

interface BedExitDetectionProps {
  enabled: boolean;
  onExitDetected: (confidence: number) => void;
  onFalseAlarm: () => void;
  bedZones: BedZone[];
  sensitivityThreshold?: number; // 0-100, higher means more sensitive
}

type BedState = 'OCCUPIED' | 'PARTIAL_EXIT' | 'FULL_EXIT' | 'RETURNED';

const BedExitDetection: React.FC<BedExitDetectionProps> = ({ 
  enabled, 
  onExitDetected, 
  onFalseAlarm, 
  bedZones,
  sensitivityThreshold = 50
}) => {
  const [currentState, setCurrentState] = useState<BedState>('OCCUPIED');
  const [stateHistory, setStateHistory] = useState<{state: BedState, timestamp: Date}[]>([]);
  const [recentPressureData, setRecentPressureData] = useState<PressureReading[]>([]);
  
  // Simulate pressure sensor data
  useEffect(() => {
    if (!enabled) return;
    
    // This interval simulates receiving pressure data from sensors
    const dataSensorInterval = setInterval(() => {
      const now = new Date();
      
      // Generate simulated pressure data
      // In a real app, this would come from actual sensors
      const simulatedReading: PressureReading = {
        timestamp: now,
        zones: bedZones.map(zone => {
          // Simulate pressure readings based on time to demonstrate transition
          // Morning time 6-8 AM would show decreasing pressure
          const isMorningHours = now.getHours() >= 6 && now.getHours() <= 8;
          
          // For demonstration, we'll just use time to simulate a bed exit
          // A real app would use actual pressure sensor data
          let simulatedPressure = 100; // Normal pressure when occupied
          
          if (isMorningHours) {
            // Simulate pressure reduction during morning hours
            // This is a simplistic simulation - in a real app, we'd use actual sensor data
            const minutesSinceSix = (now.getHours() - 6) * 60 + now.getMinutes();
            const totalMorningMinutes = 2 * 60; // 2 hours
            const progressThroughMorning = Math.min(minutesSinceSix / totalMorningMinutes, 1);
            
            // Gradual pressure decrease over the morning hours
            if (progressThroughMorning > 0.5) {
              // Sharp decrease simulating person getting out of bed
              simulatedPressure = Math.max(10, 100 - progressThroughMorning * 180);
            }
          }
          
          return {
            zoneId: zone.id,
            pressure: simulatedPressure
          };
        })
      };
      
      // Add to recent pressure data and keep only the last 10 readings
      setRecentPressureData(prev => {
        const updated = [...prev, simulatedReading].slice(-10);
        return updated;
      });
      
    }, 2000); // Simulate data coming in every 2 seconds
    
    return () => {
      clearInterval(dataSensorInterval);
    };
  }, [enabled, bedZones]);
  
  // Process sensor data and determine bed state
  useEffect(() => {
    if (!enabled || recentPressureData.length < 2) return;
    
    const determineNewState = (): BedState => {
      // Get the most recent and 2nd most recent readings
      const latestReading = recentPressureData[recentPressureData.length - 1];
      const previousReading = recentPressureData[recentPressureData.length - 2];
      
      // Calculate average pressure across all zones
      const calculateAveragePressure = (reading: PressureReading): number => {
        const sum = reading.zones.reduce((acc, zone) => acc + zone.pressure, 0);
        return sum / reading.zones.length;
      };
      
      const currentAvgPressure = calculateAveragePressure(latestReading);
      const previousAvgPressure = calculateAveragePressure(previousReading);
      
      // Get the current time
      const now = new Date();
      const isMorningHours = now.getHours() >= 5 && now.getHours() <= 9;
      
      // Adjust threshold based on sensitivity and time of day
      const baseThreshold = 30; // Base pressure threshold for bed exit
      const timeMultiplier = isMorningHours ? 1.2 : 0.8; // More sensitive during morning hours
      const sensitivityMultiplier = sensitivityThreshold / 50; // User-defined sensitivity
      
      const adjustedThreshold = baseThreshold * timeMultiplier * sensitivityMultiplier;
      
      // Determine state based on pressure change
      switch (currentState) {
        case 'OCCUPIED':
          if (currentAvgPressure < adjustedThreshold) {
            return 'FULL_EXIT';
          } else if (currentAvgPressure < previousAvgPressure * 0.7) {
            return 'PARTIAL_EXIT';
          }
          break;
          
        case 'PARTIAL_EXIT':
          if (currentAvgPressure < adjustedThreshold) {
            return 'FULL_EXIT';
          } else if (currentAvgPressure > previousAvgPressure * 1.3) {
            return 'RETURNED';
          }
          break;
          
        case 'FULL_EXIT':
          if (currentAvgPressure > adjustedThreshold * 1.5) {
            return 'RETURNED';
          }
          break;
          
        case 'RETURNED':
          if (currentAvgPressure > adjustedThreshold * 1.5) {
            return 'OCCUPIED';
          } else if (currentAvgPressure < adjustedThreshold) {
            return 'FULL_EXIT';
          }
          break;
      }
      
      return currentState;
    };
    
    const newState = determineNewState();
    
    if (newState !== currentState) {
      // Add to state history
      setStateHistory(prev => [...prev, {state: newState, timestamp: new Date()}].slice(-20));
      
      // Update current state
      setCurrentState(newState);
      
      // Trigger events based on state transitions
      if (newState === 'FULL_EXIT' && (currentState === 'OCCUPIED' || currentState === 'PARTIAL_EXIT')) {
        // Calculate confidence based on time of day and pressure consistency
        const now = new Date();
        const isMorningHours = now.getHours() >= 5 && now.getHours() <= 9;
        const timeConfidence = isMorningHours ? 80 : 50;
        
        // Check how consistent the pressure drop has been
        const consistencyConfidence = calculateConsistencyConfidence(recentPressureData);
        
        const overallConfidence = Math.min(100, (timeConfidence + consistencyConfidence) / 2);
        
        // Notify about bed exit
        onExitDetected(overallConfidence);
      } else if (newState === 'RETURNED' && currentState === 'PARTIAL_EXIT') {
        // Notify about false alarm
        onFalseAlarm();
      }
    }
  }, [recentPressureData, currentState, enabled, onExitDetected, onFalseAlarm, sensitivityThreshold]);
  
  // Helper function to calculate how consistent the pressure drop has been
  const calculateConsistencyConfidence = (readings: PressureReading[]): number => {
    if (readings.length < 3) return 50; // Not enough data
    
    // Check if pressure has been dropping consistently
    const averagePressures = readings.map(reading => {
      return reading.zones.reduce((sum, zone) => sum + zone.pressure, 0) / reading.zones.length;
    });
    
    // Calculate how many consecutive decreases we have
    let consecutiveDecreases = 0;
    for (let i = averagePressures.length - 1; i > 0; i--) {
      if (averagePressures[i] < averagePressures[i-1]) {
        consecutiveDecreases++;
      } else {
        break;
      }
    }
    
    // Convert to confidence percentage
    return Math.min(100, consecutiveDecreases * 25);
  };
  
  // Render component (visualization of bed state)
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-medium mb-3">Bed Exit Detection</h3>
      
      <div className="flex items-center mb-4">
        <div className={`px-3 py-1 rounded-full text-sm font-medium
          ${currentState === 'OCCUPIED' ? 'bg-green-100 text-green-800' : 
            currentState === 'PARTIAL_EXIT' ? 'bg-yellow-100 text-yellow-800' : 
            currentState === 'FULL_EXIT' ? 'bg-red-100 text-red-800' : 
            'bg-blue-100 text-blue-800'}`}>
          {currentState}
        </div>
        <span className="ml-2 text-sm text-gray-500">
          {enabled ? 'Monitoring active' : 'Monitoring paused'}
        </span>
      </div>
      
      <div className="text-sm space-y-2">
        <div className="grid grid-cols-2 gap-2">
          {bedZones.map(zone => (
            <div key={zone.id} className="border rounded p-2">
              <div className="font-medium">{zone.name}</div>
              <div className="text-xs text-gray-500">Threshold: {zone.pressureThreshold}</div>
              {recentPressureData.length > 0 && (
                <div className="mt-1">
                  <div className="text-xs">Current pressure:</div>
                  <div className="font-medium">
                    {recentPressureData[recentPressureData.length - 1].zones.find(z => z.zoneId === zone.id)?.pressure.toFixed(1)}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {stateHistory.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium mb-2">Recent State Changes</h4>
          <div className="text-xs space-y-1 max-h-24 overflow-y-auto">
            {stateHistory.slice().reverse().map((entry, idx) => (
              <div key={idx} className="flex justify-between">
                <span className={`${
                  entry.state === 'OCCUPIED' ? 'text-green-600' : 
                  entry.state === 'PARTIAL_EXIT' ? 'text-yellow-600' : 
                  entry.state === 'FULL_EXIT' ? 'text-red-600' : 
                  'text-blue-600'
                }`}>
                  {entry.state}
                </span>
                <span className="text-gray-500">
                  {entry.timestamp.toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BedExitDetection; 